import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Field, Form, Formik } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import PATH from 'routes/paths';
import { compose } from 'redux';
import i18n from 'i18next';
import { ReactComponent as CafmMainLogo } from 'assets/coralLogo40x40.svg';
import { ReactComponent as CafmTitle } from 'assets/coralTitle.svg';
import TextField from '../../stories/ui/TextField/TextField';
import Button from '../../stories/ui/Button/Button';
import {
  login as loginAction,
  setMe as setMeAction,
} from '../../store/auth/actions';
import AuthClientStore from '../../clientStore/AuthClientStore';
import {
  authErrorSelector,
  isAuthenticatedSelector,
} from '../../store/auth/selectors';
import useTextFieldErrorClasses from '../../hooks/styles/useTextFieldErrorClasses';
import InputErrorIcon from '../../components/ui/InputErrorIcon';
import withHelmet from '../../hocs/withHelmet';
import Alert from '../../alerts/Alert';

const PageContainer = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.palette.gray[100]};
`;

const LoginContainer = styled.div`
  width: 340px;
  min-height: 320px;
  background-color: ${({ theme }) => theme.palette.themeCommon.white.main};
  border-radius: 0.5rem;
  padding: 0 1.5rem 1.5rem;
`;

const ForgotPasswordLink = styled(Link)`
  align-self: flex-end;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.palette.primary.main};
  text-decoration: none;
  margin-top: -0.5rem;

  &:hover {
    text-decoration: underline;
  }
`;

const LoginHeader = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  height: 72px;
  font-size: 1rem;

  &:after {
    content: '';
    position: absolute;
    width: calc(100% + 3rem);
    bottom: 0;
    left: -1.5rem;
    right: -1.5rem;
    border-bottom: 1px solid
      ${({ theme }) => theme.palette.themeCommon.black.light};
  }
`;

const StyledForm = styled(Form)`
  height: calc(100% - 72px);
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
`;

const StyledTextField = styled(TextField)`
  width: 100%;
`;

const PasswordTextField = styled(StyledTextField)`
  padding-bottom: 1.5rem;
  border-bottom: 1px solid
    ${({ theme }) => theme.palette.themeCommon.black.light};
`;

const StyledSubmitButton = styled(Button)`
  font-size: 1rem;
  width: 100%;
`;

const LoginLogoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;

  svg:first-child {
    width: 80px;
    height: 80px;
  }
`;

const FooterLink = styled.a`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.gray[500]};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.palette.gray[700]};
  }
`;

export const LoginLogo = () => {
  return (
    <LoginLogoWrapper>
      <CafmMainLogo />
      <CafmTitle />
    </LoginLogoWrapper>
  );
};

function Login({
  isAuthenticated,
  authError,
  login,
  setMe,
  location: { state },
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const routerLocation = useLocation();
  const savedReturnUrl = AuthClientStore.getReturnUrl();
  let fromState = { from: { pathname: PATH.DASHBOARD } };
  if (savedReturnUrl && savedReturnUrl !== '/') {
    fromState = { from: { pathname: savedReturnUrl } };
  } else if (state?.from?.pathname && state?.from?.pathname !== '/') {
    fromState = state;
  }
  const { from } = fromState;

  useEffect(() => {
    if (isAuthenticated && savedReturnUrl) {
      AuthClientStore.removeReturnUrl();
    }
  }, [isAuthenticated, savedReturnUrl]);

  // Show the success toast set by ResetPassword.js on a successful reset,
  // then strip the query param so a refresh doesn't re-fire the toast.
  useEffect(() => {
    const params = new URLSearchParams(routerLocation.search);
    if (params.get('reset') === 'success') {
      Alert.showSuccess('loginPage.resetSuccessToast');
      params.delete('reset');
      const next = params.toString();
      history.replace({
        pathname: routerLocation.pathname,
        search: next ? `?${next}` : '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const textFieldErrorClasses = useTextFieldErrorClasses();

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email(t('invalidEmail')).required(t('enterEmail')),
    password: Yup.string().required(t('enterPasswordOrPin')),
  });

  const loginContent = (
    <PageContainer>
      <LoginLogo />
      <LoginContainer>
        <LoginHeader>
          <Trans i18nKey="enterCredentials" />
        </LoginHeader>

        <Formik
          initialValues={{
            email: '',
            password: '',
          }}
          validationSchema={LoginSchema}
          onSubmit={(values, { setSubmitting }) => {
            login({
              username: values.email,
              password: values.password,
            }).then(() =>
              setMe().then(({ payload }) => {
                !payload.organization?.id &&
                  Alert.showError(
                    'Parent organization is not set for authenticated user!'
                  );
              })
            );
            setSubmitting(false);
          }}
        >
          {() => (
            <StyledForm>
              <Field name="email">
                {({ field, meta }) => (
                  <>
                    <StyledTextField
                      type="text"
                      id="email-text-field"
                      placeholder={t('emailAddress')}
                      {...field}
                      InputProps={
                        meta.touched && meta.error
                          ? {
                              endAdornment: (
                                <InputErrorIcon
                                  title={meta.error}
                                  permanentOpen
                                />
                              ),
                              classes: textFieldErrorClasses,
                            }
                          : undefined
                      }
                    />
                  </>
                )}
              </Field>
              <Field name="password">
                {({ field, meta }) => (
                  <>
                    <PasswordTextField
                      type="password"
                      id="password-text-field"
                      placeholder={t('enterPasswordOrPin')}
                      {...field}
                      InputProps={
                        meta.touched && meta.error
                          ? {
                              endAdornment: (
                                <InputErrorIcon
                                  title={meta.error}
                                  permanentOpen
                                />
                              ),
                              classes: textFieldErrorClasses,
                            }
                          : undefined
                      }
                    />
                  </>
                )}
              </Field>
              <ForgotPasswordLink to={PATH.AUTH.FORGOT_PASSWORD}>
                <Trans i18nKey="loginPage.forgotPasswordLink" />
              </ForgotPasswordLink>
              {authError && (
                <div className="auth-errors">{authError.message}</div>
              )}

              <StyledSubmitButton
                color="primary"
                size="large"
                type="submit"
                data-testid="login.submit-btn"
              >
                <Trans i18nKey="login" />
              </StyledSubmitButton>
            </StyledForm>
          )}
        </Formik>
      </LoginContainer>
      <FooterLink
        href="/privacy-policy.pdf"
        target="_blank"
        rel="noopener noreferrer"
      >
        Privacy Policy
      </FooterLink>
    </PageContainer>
  );

  return isAuthenticated ? <Redirect to={from} /> : loginContent;
}

const mapStateToProps = (state) => ({
  isAuthenticated: isAuthenticatedSelector(state),
  authError: authErrorSelector(state),
});

const mapDispatchToProps = {
  login: loginAction,
  setMe: setMeAction,
};

Login.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  authError: PropTypes.shape({ message: PropTypes.string.isRequired }),
  login: PropTypes.func.isRequired,
  setMe: PropTypes.func.isRequired,
  location: PropTypes.shape({
    state: PropTypes.shape({
      from: PropTypes.shape({
        pathname: PropTypes.string,
      }),
    }),
  }).isRequired,
};

Login.defaultProps = {
  authError: null,
};
const wrappers = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withHelmet(i18n.t('metaTitles.login'))
);

export default wrappers(Login);
